import React, { useContext, useEffect, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import TotalBalanceBlue from "../../svg/TotalBalanceBlue";
import TotalBalanceGreen from "../../svg/TotalBalanceGreen";
import TotalBalanceOrange from "../../svg/TotalBalanceOrange";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import axiosInstance from "../../../utils/Requests/axios";
import UserContext from "../../../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import Constant from "../../Constants";
import ThemeContext from "../../../utils/Contexts/ThemeContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = (
  totalBalance,
  translatedLabel,
  bgColor,
  containerSelector
) => ({
  id: "centerText",
  beforeDraw(chart) {
    const { width, height } = chart;
    const ctx = chart.ctx;

    const container =
      document.querySelector(containerSelector) || document.documentElement;
    const cssVarValue =
      getComputedStyle(container).getPropertyValue(bgColor).trim() || "#000";

    // Defensive check
    if (!chart._metasets?.[0]?.data?.[0]) return;

    ctx.save();

    const centerX = width / 2;
    const centerY = height / 2;

    // Get inner radius and make the circle smaller
    const innerRadius = chart._metasets[0].data[0].innerRadius * 0.9;

    // Draw smaller dark circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
    ctx.fillStyle = cssVarValue;
    ctx.fill();

    ctx.restore();
    // Calculate total balance safely
    let total = 0;
    if (totalBalance) {
      const values = Object.values(totalBalance).map((v) => parseFloat(v) || 0);
      total = values.reduce((sum, v) => sum + v, 0);
    }

    // Format number as currency
    const formattedTotal =
      total % 1 === 0
        ? `$${total.toLocaleString()}`
        : `$${total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} `;

    const ismedScreen = window.innerWidth < 1920;
    const isSmallScreen = window.innerWidth < 1024;

    const mainFontSize = isSmallScreen ? 28 : ismedScreen ? 28 : 36;
    const subTextFontSize = isSmallScreen ? 14 : ismedScreen ? 14 : 16;
    ctx.fillStyle = "rgba(255, 255, 255, 1)";
    ctx.font = `700 ${mainFontSize}px sans-serif`; // 700 weight, dynamic size
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(formattedTotal, centerX, centerY - 10);

    // Draw subtext ("Total Balance")
    ctx.font = `400 ${subTextFontSize}px sans-serif`; // 400 weight, dynamic size
    ctx.fillStyle = "#6C747A";
    ctx.fillText(translatedLabel, centerX, centerY + 20);

    ctx.save();
  },
});

function TotalBalance() {
  const { t, language } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const [totalBalance, setTotalBalance] = useState(null);
  const { themeStatus } = Constant();
  const { selectedTheme } = useContext(ThemeContext);

  const totalBalanceFetched = useRef(false);
  const GetTotalBalance = () => {
    if (totalBalanceFetched.current) return;
    totalBalanceFetched.current = true;
    const url = `/users/${userData?.uuid}/wallet/balance`;
    axiosInstance
      .get(url)
      .then((response) => {
        setTotalBalance(response?.data);
      })
      .catch(() => {
        toast.error(t("There was an error fetching the wallet balance"));
      });
  };

  useEffect(() => {
    GetTotalBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = {
    labels: [t("Available"), t("On Hold"), t("Pending")],
    datasets: [
      {
        data: [
          totalBalance?.[200] || 0,
          totalBalance?.[300] || 0,
          totalBalance?.[100] || 0,
        ],
        backgroundColor: ["#17d4a3", "#f28c38", "#5c79ff"],
        borderColor: selectedTheme === "dark" ? "#25282a" : "#FFFFFF99",
        borderWidth: 3,
        cutout: "85%", // Like donut size
        borderRadius: 20, // <-- Rounded edges
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  return (
    <div
      className={`w-100 p-4 rounded custom-height-total cardSetting ${themeStatus?.[selectedTheme]}`}
      style={{ backgroundColor: "var(--bg-card)" }}
    >
      <div className="d-flex justify-content-between">
        <div id="chartDiv" className="total-graph">
          {totalBalance && (
            <Doughnut
              key={`${language}-${selectedTheme}`}
              data={data}
              options={options}
              plugins={[
                centerTextPlugin(
                  totalBalance,
                  t("Total Balance"),
                  "--graph-bg",
                  `.${themeStatus[selectedTheme]}`
                ),
              ]}
            />
          )}
        </div>
        <div className="d-flex flex-column justify-content-between">
          <div className="d-flex flex-column gap-2 mt-8 mt-md-0">
            <div className="totalBalanceLabel">
              <TotalBalanceGreen /> {t("Available")}
            </div>
            <div className="totalBalanceLabel">
              <TotalBalanceOrange /> {t("On Hold")}
            </div>
            <div className="totalBalanceLabel">
              <TotalBalanceBlue /> {t("Pending")}
            </div>
          </div>

          <div className="flex-column gap-2 total-balance-button-wrapper">
            <button
              className="btn total-balance-button d-flex justify-content-center align-items-center"
              disabled={true}
            >
              {t("Deposit")}
            </button>
            <button
              className="btn total-balance-button d-flex justify-content-center align-items-center"
              disabled={true}
            >
              {t("Withdraw")}
            </button>
          </div>
        </div>
      </div>
      <div className="gap-2 total-balance-button-wrapper-xl mt-5">
        <button
          className="btn total-balance-button align-items-center d-flex justify-content-center"
          disabled={true}
        >
          {t("Deposit")}
        </button>
        <button
          className="btn total-balance-button align-items-center d-flex justify-content-center"
          disabled={true}
        >
          {t("Withdraw")}
        </button>
      </div>
    </div>
  );
}

export default TotalBalance;
